import api from "./api";
import { ResponseContribuinteDto, UpdateContribuinteDto } from "../shared/dtos/Contribuinte.dto";
import { CreateContribuinteDto, GetContribuinteDto } from "../shared/dtos/Contribuinte.dto";
import { ResponseSearchFastContribuinte, ResponseSearchFastDependente } from "../shared/dtos/ReponseFastSearch.dto";

interface IGetContribuinteResponse {
  data: GetContribuinteDto[];
  total: number;
  position?: number | null; // Posição global do termo pesquisado (opcional)
  message: string;
}

class ContribuinteService {
  // Método para criar um novo contribuinte
  async createContribuinte(contribuinteData: CreateContribuinteDto): Promise<any> {
    try {
      const response = await api.post("/contribuinte/cadastrar", contribuinteData);
      return response.data;
    } catch (error: any) {
      console.error("Erro do backend:", error.response?.data || error.message);
      throw error; // Lança o erro para ser tratado no handleSubmit
    }
  }

  // Método para buscar um contribuinte específico por ID do titular
  async searchContribuinte(dependenteTitular: number): Promise<GetContribuinteDto> {
    return api
      .get(`/contribuinte/validar/${dependenteTitular}`)
      .then((response) => {
        return Promise.resolve(response.data);
      })
      .catch((error) => {
        console.error(
          "Erro ao buscar contribuinte:",
          error.response ? error.response.data : error.message
        );
        return Promise.reject(error);
      });
  }

  async findAll(): Promise<GetContribuinteDto[]> {
    return api
      .get(`/contribuinte/findAll`)
      .then((response) => {
        return Promise.resolve(response.data);
      })
      .catch((error) => {
        return Promise.reject(error);
      });
  }

  async getContribuinteById(id: number): Promise<GetContribuinteDto> {
    return api
      .get(`/contribuinte/find/${id}`)
      .then((response) => {
        return Promise.resolve(response.data);
      })
      .catch((error) => {
        return Promise.reject(error);
      });
  }

  async getContribuinteByIdData(id: number): Promise<GetContribuinteDto> {
    return api
      .get(`/contribuinte/find/${id}`)
      .then((response) => {
        return Promise.resolve(response.data);
      })
      .catch((error) => {
        console.error(
          "Erro ao buscar dados financeiros do contribuinte:",
          error.response ? error.response.data : error.message
        );
        return Promise.reject(error);
      });
  }

  // Método para listar todos os contribuintes
  async getAllContribuintes(
    page = 1,
    limit = 10,
    search = ""
  ): Promise<{
    data: GetContribuinteDto[];
    total: number;
    position?: number | null;
  }> {
    return api
      .get<IGetContribuinteResponse>("/contribuinte/list", {
        params: { page, limit, search },
        timeout: 0
      })
      .then((response) => ({
        data: response.data.data,
        total: response.data.total,
        position: response.data.position || null,
      }))
      .catch((error) => {
        console.error(
          "Erro ao buscar contribuintes:",
          error.response ? error.response.data : error.message
        );
        throw error;
      });
  }

  async updateDadosFinanceiros(
    id: number,
    contribuinteData: UpdateContribuinteDto
  ): Promise<ResponseContribuinteDto> {
    return api
      .patch(`/contribuinte/update/${id}`, contribuinteData)
      .then((response) => {
        return Promise.resolve(response.data);
      })
      .catch((error) => {
        throw error;
      });
  }

  async updateContribuinte(
    id: number,
    contribuinteData: UpdateContribuinteDto
  ): Promise<{ status: boolean; contribuinte: GetContribuinteDto }> {
    try {
      const response = await api.patch(`/contribuinte/update/${id}`, contribuinteData);
      return response.data as { status: boolean; contribuinte: GetContribuinteDto };
    } catch (error: any) {
      console.error("Erro do backend:", error.response?.data || error.message);
      throw error; // Lança o erro para ser tratado no handleSubmit
    }
  }

  async deleteContribuinte(id: number): Promise<any> {
    try {
      const response = await api.delete(`/contribuinte/delete/${id}`);
      return response.data; // Retorna os dados da resposta
    } catch (error: any) {
      console.error(`Erro ao excluir contribuinte com ID ${id}:`, error);
      throw new Error("Não foi possível excluir o contribuinte. Tente novamente."); // Retorna um erro mais amigável
    }
  }

  async searchCpf(cpfToSearch: string): Promise<{ status: boolean; message: string }> {
    try {
      const response = await api.get(`/contribuinte/search-cpf`, {
        params: { cpfToSearch: cpfToSearch },
      });
      return response.data as { status: boolean; message: string };
    } catch (error: any) {
      console.error(`Erro ao buscar CPF`, error);
      throw new Error("Não foi possível buscar o CPF informado."); // Retorna um erro mais amigável
    }
  }

  async getContribuinteSearchFast(search: string): Promise<{ contribuinteData: ResponseSearchFastContribuinte[], dependenteData: ResponseSearchFastDependente[] }> {
    const response = await api.get(`/contribuinte/get-contribuinte-for-search-fast/${search}`);
    return response.data;
  }

  async countContribuintesByLotacaoOrLocalPagamento(data: {
    idLocalPagamento?: number;
    idLotacao?: number;
  }): Promise<any> {
    try {
      const response = await api.post(
        `contribuinte/count/contribuintes-by-lotacao-or-local-pagamento`,
        {
          data,
          timeout: 0,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error(
        "Erro ao buscar contribuintes por lotacao ou local de pagamento:",
        error
      );
      throw error;
    }
  }
}

const contribuinteService = new ContribuinteService();
export default contribuinteService;
